<template>
  <div class="annotated-image-root" style="position: relative;">
    <div class="annotated-image__toolbar" v-if="zoom_enabled">
      <v-btn
        class="annotated-image__toolbar-btn"
        icon
        @click="resetZoom"
        color="blue lighten-2"
      >
        <v-icon>fa-solid fa-expand</v-icon>
      </v-btn>

      <div v-if="show_brightness_contrast" class="annotated-image__adjust-controls">
        <label class="annotated-image__adjust-label" :for="brightnessInputId">{{$t('annotate.brightness')}}</label>
        <input
          type="range"
          :id="brightnessInputId"
          class="annotated-image__adjust-slider"
          :value="brightness"
          min="-1"
          max="1"
          step="0.003921"
          @input="onBrightnessInput"
        >
        <label class="annotated-image__adjust-label" :for="contrastInputId">{{$t('annotate.contrast')}}</label>
        <input
          type="range"
          :id="contrastInputId"
          class="annotated-image__adjust-slider"
          :value="contrast"
          min="-1"
          max="1"
          step="0.003921"
          @input="onContrastInput"
        >
      </div>

      <div class="annotated-image__annotation-count">
        {{pretty_annotation_count}}
      </div>

      <div v-if="is_embedding_loading" style="margin-left: auto; padding-top: 11px;">
        Segment Anything
        <v-progress-circular
          :size="24"
          :width="2"
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
    </div>
    <div v-else-if="!hide_annotation_ui" height="40px;">{{pretty_annotation_count}}</div>
    <div
      class="annotated-image__canvas-wrap"
      :class="{ 'annotated-image__blur-wrap': sensitiveBlur }"
      @contextmenu.prevent
    >
      <canvas :id="canvas_id"></canvas>
    </div>
    
    <!-- Right-click popup for annotation label changes -->
    <annotation-right-click-popup
      :visible="showRightClickPopup"
      :position="popupPosition"
      :annotation="selectedAnnotationForPopup"
      @close="closeRightClickPopup"
      @update_annotation="updateAnnotationLabel"
    ></annotation-right-click-popup>
  </div>
</template>

<script>

import { markRaw } from "vue";
import BboxCanvas from "@/bbox_canvas";
import Annotation from '@/classes/annotation'
import { fabric } from "fabric";
import AnnotationRightClickPopup from "@/components/AnnotationRightClickPopup";

export default {
  name: "AnnotatedImage",
  components: {
    AnnotationRightClickPopup
  },
  props: {
    image_url: {required: true},
    zoom_enabled: {default: true},
    annotations: {required: true},
    max_height: {default: false,},
    brightness: {default: 1.0},
    contrast: {default: 1.0},
    is_embedding_loading: {default: false},
    /** When true, load image_url as-is (no auth token query param). */
    skip_image_auth_token: {default: false},
    /** When true, ignore right-click label edit popup (read-only / public views). */
    suppress_annotation_context_menu: {default: false},
    /** When true with zoom off, hide the annotation count line above the canvas. */
    hide_annotation_ui: {default: false},
    /**
     * Max drawing width in px for BboxCanvas (capped at 1400 in bbox_canvas).
     * When unset, width comes from the parent element like before.
     */
    canvas_max_width: { type: Number, default: null },
    /** When set, click/drag on the image places an annotation with this label. */
    placement_label: { type: Object, default: null },
    /** Strong blur for restricted viewers (project meta policy). */
    sensitiveBlur: { type: Boolean, default: false },
    /** Show brightness/contrast sliders in the toolbar (annotate screen). */
    show_brightness_contrast: { type: Boolean, default: false },
  },
  emits: [
    'update:brightness',
    'update:contrast',
    'update_annotation_label',
    'delete_annotation',
    'add_annotation',
    'canvas_mousedown',
    'place_on_canvas',
    'update_coords',
    'canvas_selected',
    'select',
    'unselect',
    'image_url_with_token',
    'image_loaded',
  ],
  data () {
    return {
      'canvas_id': 'custom_id',
      'canvas': false,
      'zoom_factor': 1,
      'view_port_transforms': [0,0,0,0,0,0],
      'draw_annotations': true,
      'last_annotation_added': false,
      'showRightClickPopup': false,
      'popupPosition': { x: 0, y: 0 },
      'selectedAnnotationForPopup': null,
      'pendingAnnotations': false,
      placementStart: null,
      loadSeq: 0,
    }
  },
  mounted () {
    this.canvas_id = this.getUniqueId();
    if(this.image_url) {
      this.load();
    }
  },
  beforeUnmount () {
    this.loadSeq += 1;
    this.removeEventListeners();
    if (this.canvas && this.canvas.canvas) {
      this.canvas.canvas.dispose();
    }
    this.canvas = false;
    this.draw_annotations = false;
  },
  watch: {
    brightness(v) {
      this.canvas.updateBrightness(v);
    },
    contrast(v) {
      this.canvas.updateContrast(v);
    },
    image_url() {
      this.draw_annotations = false;
      this.load();
    },
    canvas_max_width() {
      this.draw_annotations = false;
      this.load();
    },
    max_height() {
      this.draw_annotations = false;
      this.load();
    },
    placement_label() {
      this.syncPlacementMode();
    },
    annotations(n) {
      if(!this.draw_annotations) {
        this.pendingAnnotations = true;
        return;
      }
      this.pendingAnnotations = false;
      if(this.canvas) {

        let annotation_selected = false
        let anything_updated = false;

        let this2 = this;
        let existing_annotations = this.canvas.getAnnotations();
        const existingById = {};
        existing_annotations.forEach((existing) => {
          existingById[existing.id] = existing;
        });
        let new_ids = n.map(new2=>{return new2.id})
        let existing_ids = existing_annotations.map(exist=>{return exist.id})
        let selectionDirty = false;

        existing_annotations.forEach(annotation=>{
          if(!new_ids.includes(annotation.id)) {
            this2.deleteAnnotation(annotation);
            anything_updated = true;
          }
        })
        n.forEach(annotation=>{
          if(annotation.selected) {
            annotation_selected = true;
          }
          const existing = existingById[annotation.id];
          if (existing) {
            existing.selected = annotation.selected;
          }
          const newKey = BboxCanvas.annotationCanvasKey(annotation);
          const oldKey = existing ? BboxCanvas.annotationCanvasKey(existing) : null;
          const geomNew = BboxCanvas.annotationGeometryKey(annotation);
          const geomOld = existing ? BboxCanvas.annotationGeometryKey(existing) : null;
          if (newKey !== oldKey) {
            if (existing && geomNew === geomOld) {
              existing.coords = annotation.coords;
            } else {
              anything_updated = true;
              if (existing) {
                this2.deleteAnnotation(annotation);
              }
              if (annotation.show) {
                this2.addAnnotation(annotation, !existing_ids.includes(annotation.id));
              }
            }
          } else if (existing && existing.selected !== annotation.selected) {
            selectionDirty = true;
          }
        })

        if (selectionDirty && !anything_updated) {
          this2.canvas.syncSelectionFromModels(n);
        } else if(anything_updated && annotation_selected) {
          this2.hideText();
        }
        else if (anything_updated) {
          this2.showText();
        }

        if(anything_updated) {
          this2.canvas.selectAllAnnotations(this2.annotations_selected)
        }
      }
    },
    annotations_selected: {
      handler(selected) {
        if (!this.canvas || !this.draw_annotations) {
          return;
        }
        if (selected.length > 0) {
          this.canvas.selectAllAnnotations(selected);
        } else if (this.canvas) {
          this.canvas.showText();
          this.canvas.requestRenderAll();
        }
      },
      deep: true,
    },
  },
  computed: {
    canvasTagW() {
      return this.canvas_max_width != null && this.canvas_max_width > 0
        ? Math.min(this.canvas_max_width, 1400)
        : 640
    },
    canvasTagH() {
      return 500
    },
    'annotations_selected' () {
      return this.annotations.filter(a=>{return a.selected === true});
    },
    'annotation_selected' () {
      let selected = this.annotations.filter(a=>{return a.selected === true});
      if(selected.length>0) return selected[0];
      return false;
    },
    pretty_annotation_count () {
      let n = this.annotations.length;
      if (n===1) return n + ' ' + this.$t('set_label_groups.edit_annotation')
      else return n + ' ' + this.$t('set_label_groups.edit_annotations');
    },
    brightnessInputId() {
      return this.canvas_id + '-brightness';
    },
    contrastInputId() {
      return this.canvas_id + '-contrast';
    },
  },
  methods: {
    onBrightnessInput(e) {
      this.$emit('update:brightness', parseFloat(e.target.value));
    },
    onContrastInput(e) {
      this.$emit('update:contrast', parseFloat(e.target.value));
    },
    eventAnnotationRightClick(e) {
      if (this.suppress_annotation_context_menu) {
        return;
      }
      // Show popup for annotation label change
      this.selectedAnnotationForPopup = e.detail.annotation;
      this.popupPosition = {
        x: e.detail.x,
        y: e.detail.y
      };
      this.showRightClickPopup = true;
    },
    closeRightClickPopup() {
      this.showRightClickPopup = false;
      this.selectedAnnotationForPopup = null;
    },
    updateAnnotationLabel(updatedAnnotation) {
      // Find the annotation in the annotations array and update it
      const index = this.annotations.findIndex(ann => ann.id === updatedAnnotation.id);
      if (index !== -1) {
        this.annotations[index].label = updatedAnnotation.label;
        this.annotations[index].color_values = updatedAnnotation.color_values;
        // Emit the update to parent component with id and label
        this.$emit('update_annotation_label', updatedAnnotation.id, updatedAnnotation.label);
        
        // Refresh the canvas to show the updated annotation
        if (this.canvas) {
          this.canvas.requestRenderAll();
        }
      }
    },
    addEventsKeyUp: function(e) {
      let this2 = this;
      if(e.keyCode === 16) { // shift
        this2.canvas.shift_pressed = false
      }
      if(e.keyCode === 18) { // alt
        this2.canvas.alt_pressed = false
      }
      if(e.keyCode === 83) { // s
        this2.canvas.s_pressed = false
      }
    },
    addEventsKeyDown: function(e) {
      let this2 = this;
      if(e.keyCode === 16) { // shift
        this2.canvas.shift_pressed = true
      }
      if(e.keyCode === 18) { // alt
        this2.canvas.alt_pressed = true
      }
      if(e.keyCode === 83) { // s
        this2.canvas.s_pressed = true
      }
    },
    resetZoom() {
      this.canvas.resetZoom();
    },
    hideText() { // aka only label for selected
      let this2 = this;
      this2.canvas.hideText();
    },
    showText() { // aka show lables for all
      let this2 = this;
      this2.canvas.showText();
    },
    removeEventListeners() {
      window.removeEventListener('keydown', this.addEventsKeyDown);
      window.removeEventListener('keyup', this.addEventsKeyUp);
      window.removeEventListener(this.canvas_id + 'delete_annotation', this.eventDeleteAnnotation);
      window.removeEventListener(this.canvas_id + 'add_annotation', this.eventAddAnnotation);
      window.removeEventListener('update_coords', this.eventUpdateCoords);
      window.removeEventListener('select_annotation', this.eventSelectAnnotation);
      window.removeEventListener('unselect_annotation', this.eventUnselectAnnotation);
      window.removeEventListener(this.canvas_id + 'select', this.eventCanvasSelect);
      window.removeEventListener(this.canvas_id + 'canvas_selected', this.eventCanvasSelect2);
      window.removeEventListener(this.canvas_id + '_zooming', this.eventSetZoomFactor);
      window.removeEventListener(this.canvas_id + '_dragging', this.eventSetViewPointTransforms);
      window.removeEventListener(this.canvas_id + 'annotation_right_click', this.eventAnnotationRightClick);
      window.removeEventListener(this.canvas_id + 'canvas_mousedown', this.eventCanvasMouseDown);
      window.removeEventListener(this.canvas_id + 'canvas_mouseup', this.eventCanvasMouseUp);

    },
    eventSetZoomFactor(e) {
      this.zoom_factor = e.detail.zoom
    },
    eventSetViewPointTransforms(e) {
      this.view_port_transforms = e.detail.view_port_transform
    },
    eventDeleteAnnotation(e) {
      let annotation_id = e.detail.id;
      this.$emit('delete_annotation', annotation_id);
    },
    eventAddAnnotation(e) {
      // let annotation_id = e.detail.id;
      this.$emit('add_annotation', e.detail.annotation);
    },
    syncPlacementMode() {
      if (this.canvas && typeof this.canvas.setPlacementMode === 'function') {
        this.canvas.setPlacementMode(!!this.placement_label, this.placement_label);
      }
    },
    clamp01(value) {
      return Math.min(1, Math.max(0, value));
    },
    normalizePlacementCoords(coords, type) {
      if (type === 'point') {
        return [
          this.clamp01(coords[0]),
          this.clamp01(coords[1]),
        ];
      }
      const left = this.clamp01(coords[0]);
      const top = this.clamp01(coords[1]);
      const w = Math.min(1 - left, Math.max(coords[2], 0.02));
      const h = Math.min(1 - top, Math.max(coords[3], 0.02));
      return [left, top, w, h];
    },
    eventCanvasMouseDown(e) {
      const detail = e.detail;
      if (this.placement_label && detail.button === 0) {
        this.placementStart = {
          x: detail.normalized_x,
          y: detail.normalized_y,
        };
        return;
      }
      if (this.canvas.s_pressed) {
        this.$emit('canvas_mousedown', detail);
      }
    },
    eventCanvasMouseUp(e) {
      const detail = e.detail;
      if (!this.placement_label || !this.placementStart || detail.button !== 0) {
        this.placementStart = null;
        if (this.canvas && typeof this.canvas.clearPlacementPreview === 'function') {
          this.canvas.clearPlacementPreview();
        }
        return;
      }

      const type = this.placement_label.type;
      const sx = this.placementStart.x;
      const sy = this.placementStart.y;
      const ex = detail.normalized_x;
      const ey = detail.normalized_y;
      this.placementStart = null;

      let coords;
      if (type === 'point') {
        coords = [sx, sy];
      } else {
        const dx = Math.abs(ex - sx);
        const dy = Math.abs(ey - sy);
        const minDrag = 0.008;
        if (dx > minDrag || dy > minDrag) {
          const left = Math.min(sx, ex);
          const top = Math.min(sy, ey);
          coords = [left, top, dx, dy];
        } else {
          const size = 0.12;
          coords = [sx - size / 2, sy - size / 2, size, size];
        }
      }

      this.$emit('place_on_canvas', {
        type,
        coords: this.normalizePlacementCoords(coords, type),
      });
    },
    eventUpdateCoords(e) {
      this.$emit('update_coords', e.detail.id, e.detail.coords);
    },
    eventSelectAnnotation(e) {
      if(!this.zoom_enabled) {
          this.$emit('canvas_selected')
        }
        else if(e.detail.id) {
          this.$emit('select', e.detail.id, this.canvas.shift_pressed);
          this.hideText();
        }
    },
    eventUnselectAnnotation(e) {
      if(!this.zoom_enabled) {
          this.$emit('canvas_selected')
        }
        else if(e.detail.id) {
          this.$emit('unselect', e.detail.id);
        }
    },
    eventCanvasSelect(e) {
      this.canvas.selectAnnotation(e.detail.id);
    },
    eventCanvasSelect2(e) {
      // Might be the case that an annotation is selected as well
      this.$emit('canvas_selected')

      // If there is no annotation_id included, only the canvas is selected
      if(!e.detail.annotation_id) {
        this.showText();
      }
    },
    addEventListeners() {
      window.addEventListener('keydown', this.addEventsKeyDown);
      window.addEventListener('keyup', this.addEventsKeyUp);
      window.addEventListener(this.canvas_id + 'delete_annotation', this.eventDeleteAnnotation);
      window.addEventListener(this.canvas_id + 'add_annotation', this.eventAddAnnotation);
      window.addEventListener('update_coords', this.eventUpdateCoords);
      window.addEventListener('select_annotation', this.eventSelectAnnotation);
      window.addEventListener('unselect_annotation', this.eventUnselectAnnotation);
      window.addEventListener(this.canvas_id + 'select', this.eventCanvasSelect);
      window.addEventListener(this.canvas_id + 'canvas_selected', this.eventCanvasSelect2);
      window.addEventListener(this.canvas_id + '_zooming', this.eventSetZoomFactor);
      window.addEventListener(this.canvas_id + '_dragging', this.eventSetViewPointTransforms);
      window.addEventListener(this.canvas_id + 'annotation_right_click', this.eventAnnotationRightClick);
      window.addEventListener(this.canvas_id + 'canvas_mousedown', this.eventCanvasMouseDown);
      window.addEventListener(this.canvas_id + 'canvas_mouseup', this.eventCanvasMouseUp);

    },
    addAnnotation(annotation, newly_added) {
      //Meerdere add Annotation functies?
      if(newly_added) {
        this.last_annotation_added = annotation;
        annotation.selected = true;
      }
      this.canvas.addAnnotation(annotation, newly_added);
    },
    deleteAnnotation(annotation) {
      this.canvas.deleteAnnotation(annotation);
    },
    addBox() {
      let id = this.getUniqueId();
      let coords = [40, 40, 100, 140]
      let annotation = new Annotation(id, 'box', coords, 'Label', 1.0, [180,0,0]);
      this.addAnnotation(annotation);
    },
    addPoint() {
      let id = this.getUniqueId();
      let coords = [140, 120]
      let annotation = new Annotation(id, 'point', coords, 'Label', 1.0, [180,0,0]);
      this.addAnnotation(annotation);
    },
    getUniqueId() {
      return 'canvas' + Math.random().toString(36).slice(2);
    },
    getCanvasParentWidth() {
      const el = document.getElementById(this.canvas_id);
      if (!el || !el.parentElement) {
        return 0;
      }
      const wrapWidth = el.parentElement.clientWidth;
      const root = el.closest('.annotated-image-root');
      const rootWidth = root ? root.clientWidth : 0;
      return Math.max(wrapWidth, rootWidth);
    },
    redrawAnnotations() {
      if (!this.canvas || !this.draw_annotations) {
        return;
      }
      const existing = this.canvas.getAnnotations();
      existing.forEach((annotation) => {
        this.deleteAnnotation(annotation);
      });
      this.annotations.forEach((annotation) => {
        if (annotation.show) {
          this.addAnnotation(annotation, false);
        }
      });
      if (this.annotations_selected.length > 0) {
        this.canvas.selectAllAnnotations(this.annotations_selected);
      }
    },
    load() {
      let this2 = this;
      this2.loadSeq += 1;
      const loadSeq = this2.loadSeq;
      this2.removeEventListeners();
      if (this2.canvas && this2.canvas.canvas) {
        this2.canvas.canvas.dispose();
        this2.canvas = false;
        this2.zoom_factor = 1;
        this2.view_port_transforms = [0,0,0,0,0,0];
      }
      this2.draw_annotations = false;
      this2.pendingAnnotations = false;

      let image_url_with_token = this.skip_image_auth_token
        ? this.image_url
        : (this.image_url + '?token=' + localStorage.token);
      if (image_url_with_token) {
        this.$emit('image_url_with_token', image_url_with_token);
      }

      const initCanvas = (attempt) => {
        if (loadSeq !== this2.loadSeq) {
          return;
        }
        this2.$nextTick(() => {
          if (loadSeq !== this2.loadSeq) {
            return;
          }
          const canvasEl = document.getElementById(this2.canvas_id);
          if (!canvasEl || !canvasEl.parentElement) {
            if (attempt < 40) {
              requestAnimationFrame(() => initCanvas(attempt + 1));
            }
            return;
          }
          const parentW = this2.getCanvasParentWidth();
          if (parentW < 80 && attempt < 40) {
            requestAnimationFrame(() => initCanvas(attempt + 1));
            return;
          }
          fabric.Image.fromURL(image_url_with_token, function(image, err) {
            if (loadSeq !== this2.loadSeq) {
              return;
            }
            if (err) {
              console.log('error', err);
              return;
            }
            const el = document.getElementById(this2.canvas_id);
            if (!el || !el.parentElement) {
              return;
            }

            const maxW = this2.canvas_max_width != null && this2.canvas_max_width > 0
              ? this2.canvas_max_width
              : false;
            // markRaw: Vue 3's Proxy-based reactivity would otherwise wrap the
            // Fabric canvas and its objects, breaking Fabric's internal identity
            // checks (e.g. _findTargetCorner's `canvas._activeObject === this`),
            // which silently disables scale/resize controls.
            this2.canvas = markRaw(new BboxCanvas(this2.canvas_id, image, maxW, false, !this2.zoom_enabled, this2.max_height));
            this2.syncPlacementMode();
            this2.addEventListeners();
            this2.draw_annotations = true;
            this2.redrawAnnotations();
            this2.pendingAnnotations = false;
            this2.$emit('image_loaded');
          });
        });
      };
      initCanvas(0);
    }
  }
}
</script>

<style scoped>
.annotated-image-root {
  width: 100%;
  max-width: 100%;
}
.annotated-image__toolbar {
  position: relative;
  z-index: 2;
  min-height: 48px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  flex-shrink: 0;
  background: rgb(var(--v-theme-surface, 255 255 255));
}
.annotated-image__toolbar-btn {
  margin: 0;
  flex-shrink: 0;
}
.annotated-image__canvas-wrap {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  line-height: 0;
}
.annotated-image__canvas-wrap :deep(.canvas-container) {
  margin-top: 0 !important;
}
.annotated-image__adjust-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.annotated-image__adjust-label {
  font-size: 12px;
  white-space: nowrap;
  margin: 0;
}
.annotated-image__adjust-slider {
  width: 96px;
  margin: 0;
}
.annotated-image__annotation-count {
  padding-top: 2px;
}
.annotated-image__blur-wrap >>> canvas {
  filter: blur(28px);
  transform: scale(1.02);
}
</style>